import {
  Component,
  DestroyRef,
  Input,
  InputSignal,
  ModelSignal,
  OnDestroy,
  Signal,
  WritableSignal,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import {
  FilegroupStore,
  PhotoFlags,
  profilePhotoByFlagKey,
} from '@shared/filegroup/data-access';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { getCyclicColor, getNameInitial } from '@shared/utils';
import { TooltipFollowDirective } from '@shared/directives';

/**
 * Defines the avatar style types.
 * @typedef {'Square' | 'Circle'} TAvatarStyle
 */

/**
 * AvatarComponent displays a user's profile photo.
 * It supports editable mode, memory caching, different sizes, and styles.
 *
 * @component
 * @selector lib-avatar
 *
 * @example
 * <lib-avatar
 *   [personId]="'user123'"
 *   [isEditable]="true"
 *   [isPhotoInMemory]="false"
 *   [size]="'lg'"
 *   [src]="'https://example.com/path/to/photo.jpg'"
 *   [style]="'Circle'">
 * </lib-avatar>
 */

type TAvatarStyle = 'Square' | 'Circle';

export type TAvatarSize = 'xs' | 'sm' | 'md' | 'default' | 'lg';

export type TAvatarStatus = 'active' | 'inactive';

/**
 * Defines the avatar type.
 * @typedef {'user' | 'default'} TAvatarType
 */
export type TAvatarType = 'user' | 'default';

@Component({
  selector: 'lib-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  standalone: true,
  imports: [NgClass, NgStyle, TooltipFollowDirective, NgTemplateOutlet],
})
export class AvatarComponent implements OnDestroy {
  /**
   * Indicates whether the avatar is being used in anonymous context (without authentication).
   *
   * @input
   * @type {boolean}
   * @default false
   */
  public readonly isAnon: InputSignal<boolean> = input<boolean>(false);
  /**
   * Unique identifier for the person whose profile photo is displayed.
   * When set, it assigns the identifier to `groupId` and triggers the
   * retrieval of the profile photo via the FilegroupStore.
   *
   * @input
   * @param {string} id - The unique person identifier.
   */
  @Input() set personId(id: string | undefined | null) {
    this._personId = id ?? null;
    this.fetchProfilePhoto();
  }

  /**
   * Backing field for the {@link personId} input.
   */
  private _personId: string | null = null;

  /**
   * Resolves and fetches the profile photo based on the current `personId`,
   * `flag` and `isAnon` inputs. Invoked from the `personId` and `flag` setters
   * so binding order does not matter. The store fetch methods are guarded, so
   * running this twice (once per input) is safe.
   */
  private fetchProfilePhoto(): void {
    const id = this._personId;
    const nextGroupId = id ?? '';

    // Reset the load gate whenever the avatar points at a different photo
    // source (e.g. a recycled component in a virtualized grid switching
    // person). Without this, a reused instance keeps a stale `loaded` state.
    if (nextGroupId !== this.groupId) {
      this.loaded.set(false);
    }

    this.groupId = nextGroupId;

    if (!id) {
      this.loaded.set(false);
      return;
    }

    if (this.isAnon()) {
      this.filegroupStore.getActuarProfilePhotoAsync({
        id,
        cancelToken: this.destroyRef,
      });
      return;
    }

    if (this._flag != null) {
      this.groupId = profilePhotoByFlagKey(id, this._flag);
      this.filegroupStore.getProfilePhotoByFlag({
        id,
        flag: this._flag,
        cancelToken: this.destroyRef,
      });
      return;
    }

    this.filegroupStore.getActuarProfilePhoto({
      id,
      cancelToken: this.destroyRef,
    });
  }

  /**
   * Optional photo flag fetched for `personId` (e.g. `PhotoFlags.AppProfile`
   * for feed-author photos, which live under `/UserPhotos?groupId&flag`). When
   * unset the default ActuarProfile lookup is used, so existing callers are
   * unaffected.
   *
   * @input
   * @type {PhotoFlags}
   */
  @Input() set flag(value: PhotoFlags | undefined) {
    this._flag = value;
    this.fetchProfilePhoto();
  }

  /**
   * Backing field for the {@link flag} input.
   */
  private _flag: PhotoFlags | undefined;
  /**
   * Indicates whether the avatar is editable.
   *
   * @input
   * @default false
   */
  @Input() isEditable = false;

  /**
   * Determines if the avatar image is already stored in memory.
   *
   * @input
   * @default false
   */
  @Input() isPhotoInMemory = false;

  @Input() set index(index: number) {
    if (index) {
      this.backgroundColor.set(getCyclicColor(index));
    }
  }

  /**
   * Controls the size of the avatar.
   *
   * @input
   * @type {TAvatarSize}
   * @default 'large'
   */
  public size: InputSignal<TAvatarSize> = input<TAvatarSize>('default');

  /**
   * Controls custom the size of the avatar.
   *
   * Value can be a string or a number.
   * Number or String, is interpreted as a pixel value.
   *
   * @input
   * @type { string | number }
   * @default undefined
   */
  public customSize: InputSignal<string | number> = input<string | number>(
    null
  );

  /**
   * Injected service for accessing file group functionalities, including
   * fetching the user's profile photo.
   */
  public filegroupStore = inject(FilegroupStore);

  /**
   * Injected DestroyRef for managing component lifecycle and request cancellation.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Stores the group id associated with the person.
   */
  public groupId: string;

  /**
   * Indicates whether the avatar image has finished loading. Kept as a signal
   * so the `(load)` / `(error)` callbacks reliably schedule change detection
   * even when the component is rendered outside Angular's zone (e.g. inside a
   * DevExtreme grid cell template), where a plain field write would leave the
   * placeholder stuck over an already-loaded photo.
   */
  public readonly loaded: WritableSignal<boolean> = signal(false);

  /**
   * Reveals the image once the browser reports it has loaded.
   */
  public onImageLoaded(): void {
    this.loaded.set(true);
  }

  /**
   * Falls back to the placeholder when the image fails to load, so a broken or
   * transiently-failing URL never leaves a blank avatar.
   */
  public onImageError(): void {
    this.loaded.set(false);
  }

  /**
   * Source URL or data string for the avatar image.
   *
   * @input
   * @type {string}
   */
  public readonly src: InputSignal<string> = input<string>();

  /**
   * Determines the shape style of the avatar.
   *
   * @input
   * @type {TAvatarStyle}
   * @default 'Circle'
   */
  public readonly style: InputSignal<TAvatarStyle> =
    input<TAvatarStyle>('Circle');

  /**
   * The FirstName of user to display the initial when there`s no image or gender.
   *
   * @input
   * @type {string}
   * @default null
   */
  public readonly firstName: InputSignal<string> = input<string>(null);

  /**
   * The FirstName of user to display the initial when there`s no image or gender.
   *
   * @input
   * @type {string}
   * @default null
   */
  public readonly lastName: InputSignal<string> = input<string>(null);

  /**
   * The background-color to display the initial of firstName when there`s
   * no image
   *
   * @input
   * @type {string}
   * @default null
   */
  public readonly backgroundColor: ModelSignal<string> =
    model<string>('bg-purple');

  /**
   *
   * The avatar status.
   * @input
   * @type {TAvatarStatus}
   * @default null
   */
  public readonly status: InputSignal<TAvatarStatus> =
    input<TAvatarStatus>(null);

  /**
   * Disables the avatar tooltip.
   *
   * @input
   * @type {boolean}
   * @default false
   */
  public readonly tooltipFollowDisabled: InputSignal<boolean> =
    input<boolean>(false);

  /**
   * The z-index of the tooltip.
   *
   * @input
   * @type {number}
   * @default 1000
   */
  public readonly tooltipFollowZIndex: InputSignal<number> =
    input<number>(1000);

  /**
   * The avatar sizes.
   */
  public readonly avatarSizes: Record<TAvatarSize, number> = {
    xs: 20,
    sm: 40,
    md: 32,
    default: 48,
    lg: 203,
  };

  public readonly profileSizes: Record<
    TAvatarSize,
    { width: number; height: number }
  > = {
    xs: { width: 13, height: 16 },
    sm: { width: 26, height: 32 },
    md: { width: 21, height: 26 },
    default: { width: 31, height: 39 },
    lg: { width: 132, height: 167 },
  };
  /**
   * The avatar status.
   */
  public readonly avatarStatus: Record<TAvatarStatus, string> = {
    active: 'rounded-full border-[2px] border-success',
    inactive: 'rounded-full border-[2px] border-danger',
  };

  /**
   * The avatar type.
   *
   * @input
   * @type {TAvatarType}
   * @default EAvatarType.Default
   */
  public readonly type: InputSignal<TAvatarType> =
    input<TAvatarType>('default');

  /**
   * First letter of {@link firstName}, ignoring anything that is not a letter.
   * Names arrive as the person typed them (`'Marcrusk (Marco Nunes'`), so the
   * raw first character would render a `(` in the avatar as if it were an
   * initial.
   */
  public readonly firstInitial: Signal<string> = computed(() =>
    getNameInitial(this.firstName())
  );

  /**
   * First letter of {@link lastName}, under the same rule as
   * {@link firstInitial}.
   */
  public readonly lastInitial: Signal<string> = computed(() =>
    getNameInitial(this.lastName())
  );

  /**
   * The type actually rendered. A `'user'` avatar with no usable initial
   * (empty names, or names made only of symbols) would paint an empty coloured
   * circle, so it falls back to the generic silhouette.
   */
  public readonly resolvedType: Signal<TAvatarType> = computed(() =>
    this.type() === 'user' && !this.firstInitial() && !this.lastInitial()
      ? 'default'
      : this.type()
  );

  /**
   * Fraction of the avatar diameter the initials may use as font size. Two
   * letters are laid out side by side, so the pair — up to ~1.7em wide for
   * `'MA'` / `'WW'` — is what has to fit inside the circle, not a single glyph.
   * Consumed by `.avatar-initials` as the upper bound on the design-kit size.
   */
  public readonly initialsFontRatio: Signal<number> = computed(() =>
    this.firstInitial() && this.lastInitial() ? 0.42 : 0.52
  );

  /**
   * OnDestroy lifecycle hook implementation.
   * Currently empty as per task requirements.
   */
  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnDestroy(): void {
    // Empty implementation for now - will be enhanced in subsequent tasks
  }
}
